import { createActions, createReducer } from "reduxsauce"
import { takeEvery, put, delay, select } from "redux-saga/effects"
import { v4 as uuid } from "uuid"
import API_MESSAGES from "api/messages"
import { track } from "helpers/analytics"
import { getIsInternalAccount } from "modules/home-layout/selectors"
import { NOTIFICATION_SHOWN_EVENT } from "./constants"

export const { Types, Creators } = createActions({
  add: (level, title, message, tracked = false) => ({ type: "ompnt/modules/notifications/ADD", level, title, message, tracked, id: uuid() }),
  addApiNotification: (level, title, code, message) => ({ type: "ompnt/modules/notifications/ADD_API_NOTIFICATION", level, title, code, id: uuid(), message }),
  dismiss: ["id"],
}, {
  prefix: "ompnt/modules/notifications/",
})

const INITIAL_STATE = {
  notifications: [],
}

export const MAX_VISIBLE_NOTIFICATIONS = 3

const TRANSIENT_OVERFLOW_ALLOWANCE = 1

function isSameNotification(first, second) {
  return first.level === second.level
    && (first.title ?? null) === (second.title ?? null)
    && first.message === second.message
}

const AUTO_DISMISSED_LEVELS = ["info", "success"]

function isAutoDismissedLevel(level) {
  return AUTO_DISMISSED_LEVELS.includes(level)
}

function isTransientNewcomerWithinOverflowAllowance(visibleCount, newcomer) {
  return isAutoDismissedLevel(newcomer.level) && visibleCount <= MAX_VISIBLE_NOTIFICATIONS + TRANSIENT_OVERFLOW_ALLOWANCE
}

function evictOldestNonErrors(notifications) {
  const visible = [...notifications]
  const newcomer = notifications.at(-1)
  while (visible.length > MAX_VISIBLE_NOTIFICATIONS && !isTransientNewcomerWithinOverflowAllowance(visible.length, newcomer)) {
    const oldestTransientIndex = visible.findIndex(({ level }) => isAutoDismissedLevel(level))
    const oldestNonErrorIndex = oldestTransientIndex === -1
      ? visible.findIndex(({ level }) => level !== "error")
      : oldestTransientIndex
    if (oldestNonErrorIndex === -1) {
      break
    }
    visible.splice(oldestNonErrorIndex, 1)
  }
  return visible
}

function addNotification(state, notification) {
  if (state.notifications.some(existing => isSameNotification(existing, notification))) {
    return state
  }
  return { ...state, notifications: evictOldestNonErrors([...state.notifications, notification]) }
}

function handleAdd(state, { id, level, title, message, tracked }) {
  return addNotification(state, { id, level, title, message, ...(tracked && { tracked }) })
}

function handleAddApiNotification(state, { id, level, title, code, message }) {
  return addNotification(state, { id, level, title, message: API_MESSAGES[code] || message || "An unexpected error occurred. Please try again." })
}

function handleDismiss(state, { id }) {
  return { ...state, notifications: state.notifications.filter(notification => notification.id !== id) }
}

export const HANDLERS = {
  [Types.ADD]: handleAdd,
  [Types.ADD_API_NOTIFICATION]: handleAddApiNotification,
  [Types.DISMISS]: handleDismiss,
}

export default createReducer(INITIAL_STATE, HANDLERS)

function* dismissNotifications(action) {
  const { id, level, alert } = action

  if (isAutoDismissedLevel(level) || isAutoDismissedLevel(alert?.status)) {
    // Needs to be the same as the delay in toaster.styl
    yield delay(8000)
    yield put(Creators.dismiss(id))
  }
}

function* trackNotificationShown({ id, level, title, message, tracked }) {
  if (!tracked) {
    return
  }
  const hasLanded = yield select(state => state.modules.notifications.notifications.some(notification => notification.id === id))
  if (!hasLanded) {
    return
  }
  const isInternalAccount = yield select(getIsInternalAccount)
  if (isInternalAccount) {
    return
  }
  track(NOTIFICATION_SHOWN_EVENT, { level, title: title ?? null, message })
}

export function* saga() {
  yield takeEvery([Types.ADD, Types.ADD_API_NOTIFICATION], dismissNotifications)
  yield takeEvery(Types.ADD, trackNotificationShown)
}
